/**
 * ArTable UI State
 *
 * Valtio proxy for ephemeral ArTable UI state (not persisted to Firestore).
 * Includes selection state, cell focus state, and other UI concerns.
 *
 * Created once per ArTable instance via createArTableState().
 * Follows the same pattern as store/state/createCollectionState.ts
 */

import { proxy, ref } from 'valtio';
import type { DocumentSnapshot } from 'firebase/firestore';
import type { ArRowId, TypeId } from '#types/System.Artifacts.types';
import { AR_TABLE_PAGE_SIZE } from '#types/System.Parameters.types';
import type { ArTableDeriveProjection } from '../derives/resolveArTableDeriveProjections';

/**
 * Row selection state.
 * Uses Record for granular Valtio reactivity per row.
 */
type SelectionState = {
  /** Map of row ID to selected state (true = selected) */
  selectedRows: Record<ArRowId, true>;
  /** Ordered list of selected row IDs for fast iteration (no Object.keys) */
  selectedRowIds: ArRowId[];
  /** Index map for O(1) removals from selectedRowIds */
  selectedRowIndexById: Record<ArRowId, number>;
  /** Count of selected rows — O(1) access for header checkbox */
  selectedCount: number;
  /** Last clicked row for shift+click range selection */
  lastClickedRowId: ArRowId | null;
  /**
   * Which rows the bulk actions act on. `'visible'` means the ticks above,
   * intersected with `selectableRowIds`. `'all'` means every row the table
   * holds — hidden by a split, filter or view, and on other pages too.
   *
   * Under `'all'` the ticks are left exactly as they are: the grid still paints
   * from them, and the flag alone travels to the server, which enumerates. A
   * 1,500-row selection cannot be a list of ids — `AR_TABLE_PAGE_SIZE` caps the
   * ticks and the swap-pop index bookkeeping assumes that size — so this
   * mirrors `WorkflowRowScope`, which made the same call for workflows
   * (SYD-2322).
   *
   * Only `ArSelectionScopeBar` writes it, and only while every visible row is
   * ticked; `clearSelection` puts it back to `'visible'`.
   */
  scope: 'visible' | 'all';
};

/**
 * Cell focus state (keyboard navigation / active cell coordinate).
 *
 * Purpose:
 * - Stores a single "active cell" coordinate for the whole table.
 * - Used by keyboard navigation to compute the next target cell (Tab / arrows).
 *
 * Notes:
 * - In the current UX, cells are "always edit on focus" (focusing a cell focuses its editor).
 * - This state does NOT imply a cell is actively being edited; it's the navigation anchor.
 */
type CellFocusState = {
  /** Active cell coordinates (rowId + column typeId). Null = no active cell yet. */
  focusedCell: { rowId: ArRowId; columnId: TypeId } | null;
};

/**
 * Cell editing state (per-cell UI flag).
 *
 * Purpose:
 * - Tracks which specific cell editors are currently focused / "editing".
 * - Used for per-cell UI (e.g., outlining the focused cell) and for interactions that
 *   depend on focus/blur events (bulk edit preview logic, etc).
 *
 * Why a Record keyed by cellId:
 * - Enables granular Valtio reactivity per cell (only the changed cell re-renders).
 * - Key is a stable DOM id (e.g., `artable-cell-${columnId}-${rowId}`).
 */
type CellEditingState = {
  editingCellsByKey: Record<string, true>;
};

/**
 * Pending bulk-edit confirmation shown when a cell is focused
 * with >= BULK_CONFIRM_THRESHOLD selected rows.
 */
/**
 * `targetRows` is already lock-filtered, so it — not the raw selection — is what
 * the confirmation prompt must count. A stored count would be a second source of
 * truth for the same question and could disagree with the write.
 */
type BulkEditConfirmation = {
  columnId: TypeId;
  rowId: ArRowId;
  targetRows: Record<ArRowId, true>;
};

/**
 * Bulk edit preview state at the cell level.
 * Keyed by column so only cells in the relevant column re-render.
 */
type BulkEditPreviewState = {
  byColumnId: Partial<
    Record<
      TypeId,
      {
        sourceRowId: ArRowId;
        targetRows: Record<ArRowId, true>;
      }
    >
  >;
  /** Non-null while the confirmation modal is open; null otherwise. */
  pendingConfirmation: BulkEditConfirmation | null;
};

/**
 * Bundle UI state (ephemeral collapse tracking).
 * Keyed by bundleId (= header row docId).
 */
type BundleUIState = {
  collapsedBundles: Record<string, true>;
};

/**
 * Filtering UI state.
 * Single string filter per column. Visibility-only, does not affect persisted data.
 */
type FilterState = {
  /** Active filter value per column typeId (empty string = no filter) */
  byColumnId: Record<TypeId, string>;
};

/**
 * Pagination state for cursor-based Firestore pagination.
 */
type PaginationState = {
  /** 1-based current page */
  currentPage: number;
  /** Rows per page */
  pageSize: number;
  /** Total row count from getCountFromServer (excludes aggregation row) */
  totalRows: number;
  /** Derived: Math.ceil(totalRows / pageSize) */
  totalPages: number;
};

/**
 * Non-reactive cursor cache for pagination.
 * Wrapped with ref() to prevent Valtio from proxying DocumentSnapshot objects.
 * Key = page number, Value = last DocumentSnapshot of that page.
 *
 * `orderByKey` is the `field:direction` the cached snapshots were paged under.
 * A cursor is invalid under any other ordering, so `useArTablePagination`
 * empties the cache the moment the two disagree. Empty string = never paged.
 */
type PaginationCursorCache = {
  byPage: Map<number, DocumentSnapshot>;
  orderByKey: string;
};

/**
 * Export/import modal open state.
 *
 * One boolean per export modal (email, PDF export, CSV import). Menu items
 * flip these flags; the single `<ArTableExportModals />` host subscribes to
 * this slice only, so typing in unrelated form fields never re-renders the
 * modal subtree.
 */
type ExportModalsState = {
  email: boolean;
  pdf: boolean;
  import: boolean;
};

/**
 * Complete ArTable UI state.
 * All ephemeral state that doesn't persist to Firestore.
 */
type ArTableUIState = {
  /** Row selection state */
  selection: SelectionState;
  /** Cell focus state */
  cellFocus: CellFocusState;
  /** Cell editing state */
  cellEditing: CellEditingState;
  /** Bulk edit preview state */
  bulkEditPreview: BulkEditPreviewState;
  /** Bundle collapse state */
  bundles: BundleUIState;
  /** Filtering state */
  filtering: FilterState;
  /** Pagination state */
  pagination: PaginationState;
  /** Non-reactive cursor cache for pagination (ref-wrapped, not proxied) */
  paginationCursors: PaginationCursorCache;
  /** Rendered row IDs in display order (for shift+click range selection) */
  renderedRowIds: ArRowId[];
  /**
   * The rows select-all governs, and the scope every bulk consumer intersects
   * its ticks against via `resolveSelectionScope`: every rendered row, plus
   * the members of every rendered bundle header when collapse or the visibility
   * view is all that leaves them unpainted. Members ride along because
   * `arTableRowOperations` deletes exactly the ids it is handed — only duplicate
   * is bundle-aware — so a ticked lone header would strand them.
   *
   * A header whose members a SPLIT or COLUMN FILTER withheld contributes
   * nothing, neither itself nor its members: the user narrowed to specific rows,
   * and taking the header would delete it out from under the rows the narrowing
   * hid. Written only by the interactive body, never the PDF-export one.
   *
   * Distinct from `renderedRowIds`, the shift+click range space, which holds
   * only what was actually painted. Also distinct from `resolveArTableVisibleRows`,
   * which answers the whole-table question: the two agree on split, filters and
   * view, and diverge on pagination and collapse — each correct for its own
   * question, not an oversight.
   */
  selectableRowIds: ArRowId[];
  /** Rendered column IDs in display order (keyboard navigation traversal space) */
  renderedColIds: TypeId[];
  /**
   * The Form Derive row projections that fill this table, published by
   * `useArTableDeriveProjections` for the surfaces props cannot reach: the
   * projected cell's tooltip, the blocked row delete and the managed-row count.
   * Normally empty — a table no derive projects into carries no entry, and every
   * consumer resolves a row through `deriveProjectionOfRow`, never through the
   * row's own `deriveOwner` marker.
   */
  deriveProjections: ArTableDeriveProjection[];
  /** Open state for the export/import modals */
  exportModals: ExportModalsState;
};

/**
 * Creates a new ArTable UI state proxy.
 *
 * Selection lives inside the state. When the table is rendered inside a form,
 * the proxy is owned by `formStore.state.tables` (keyed by `${parentPath}#${tableId}`)
 * so out-of-tree consumers can read selection via `formStore.getTableSlot`.
 */
function createArTableState(): ArTableUIState {
  return proxy<ArTableUIState>({
    selection: {
      selectedRows: {},
      selectedRowIds: [],
      selectedRowIndexById: {},
      selectedCount: 0,
      lastClickedRowId: null,
      scope: 'visible',
    },
    cellFocus: {
      focusedCell: null,
    },
    cellEditing: {
      editingCellsByKey: {},
    },
    bulkEditPreview: {
      byColumnId: {},
      pendingConfirmation: null,
    },
    bundles: {
      collapsedBundles: {},
    },
    filtering: {
      byColumnId: {},
    },
    pagination: {
      currentPage: 1,
      pageSize: AR_TABLE_PAGE_SIZE,
      totalRows: 0,
      totalPages: 0,
    },
    paginationCursors: ref({ byPage: new Map(), orderByKey: '' }),
    renderedRowIds: [],
    selectableRowIds: [],
    renderedColIds: [],
    deriveProjections: [],
    exportModals: {
      email: false,
      pdf: false,
      import: false,
    },
  });
}

export { createArTableState };
export type {
  ArTableUIState,
  SelectionState,
  CellFocusState,
  CellEditingState,
  BulkEditConfirmation,
  BulkEditPreviewState,
  BundleUIState,
  FilterState,
  PaginationState,
  PaginationCursorCache,
  ExportModalsState,
};
